import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Landmark, Plus, Trash2, Save, ArrowUp, ArrowDown, Star, AlertTriangle } from "lucide-react";
import api, { formatDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import SearchableSelect from "@/components/SearchableSelect";
import { INDONESIAN_BANKS } from "@/lib/banks";

const emptyRow = { id: null, bank_name: "", account_no: "", dirty: true };

export default function BankAccountsEditor({ endpoint = "/auth/bank-accounts", canEdit = true }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [companyBankNames, setCompanyBankNames] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(endpoint);
      setRows((data || []).map((a) => ({ ...a, dirty: false })));
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  }, [endpoint]);
  useEffect(() => { load(); }, [load]);

  // Fetch fee-free bank list = distinct bank names in shared company_bank_accounts (managed
  // by BOD + FAT-L2 in Settings). Any personal bank matching this list is considered
  // "same as company" → no admin transfer fee warning.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/company-bank-accounts");
        setCompanyBankNames(Array.from(new Set((data || []).map((b) => b.bank_name))));
      } catch (e) { console.warn("Could not load company bank names", e); setCompanyBankNames([]); }
    })();
  }, []);
  const feeFreeSet = useMemo(() => new Set(companyBankNames.map((n) => n.trim().toLowerCase())), [companyBankNames]);

  const updateRow = (i, patch) => setRows((s) => s.map((r, idx) => idx === i ? { ...r, ...patch, dirty: true } : r));
  const addRow = () => setRows((s) => [...s, { ...emptyRow }]);
  const removeRow = async (i) => {
    const row = rows[i];
    if (row.id) {
      if (!window.confirm(`Hapus rekening ${row.bank_name} — ${row.account_no}?`)) return;
      try { await api.delete(`${endpoint}/${row.id}`); toast.success("Rekening dihapus"); await load(); }
      catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
    } else {
      setRows((s) => s.filter((_, idx) => idx !== i));
    }
  };

  const move = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= rows.length) return;
    const next = [...rows];
    [next[i], next[j]] = [next[j], next[i]];
    setRows(next);
    // Persist order
    try {
      await api.patch(endpoint, { order: next.filter((r) => r.id).map((r) => r.id) });
      if (i === 0 || j === 0) toast.success("Rekening utama diperbarui");
    } catch (e) { /* silent */ }
  };

  const save = async (i) => {
    const row = rows[i];
    if (!row.bank_name?.trim() || !row.account_no?.trim()) { toast.error("Nama bank & nomor rekening wajib diisi"); return; }
    setBusy(true);
    try {
      if (row.id) {
        await api.patch(`${endpoint}/${row.id}`, { bank_name: row.bank_name.trim(), bank_account_no: row.account_no.trim() });
      } else {
        await api.post(endpoint, { bank_name: row.bank_name.trim(), bank_account_no: row.account_no.trim() });
      }
      toast.success("Rekening disimpan");
      await load();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-2" data-testid="bank-accounts-editor">
      <div className="text-[10px] text-slate-500 flex items-center gap-1 mb-1">
        <Star size={11} className="text-amber-500 fill-amber-400" />
        Rekening pertama = rekening utama untuk transfer gaji. Klik ↑ untuk memindahkan rekening ke posisi utama.
      </div>
      {rows.length === 0 && (
        <div className="text-[11px] text-slate-400 italic border border-dashed border-slate-200 p-3 text-center">Belum ada rekening tersimpan.</div>
      )}
      {rows.map((r, i) => {
        const isCompanyBank = !!(r.bank_name && feeFreeSet.has(String(r.bank_name).trim().toLowerCase()));
        const isPrimary = i === 0 && r.id;
        return (
          <div key={r.id || `new-${i}`} className={`border p-2 grid grid-cols-1 md:grid-cols-12 gap-2 items-center ${isPrimary ? "border-amber-400 bg-amber-50/50" : "border-slate-200 bg-white"}`} data-testid={`bank-row-${i}`}>
            <div className="md:col-span-1 flex items-center justify-center">
              {isPrimary ? (
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-amber-500 text-white text-[9px] font-semibold uppercase tracking-wider">
                  <Star size={10} className="fill-white" /> Utama
                </span>
              ) : (
                <span className="text-[9px] text-slate-400 font-mono">#{i + 1}</span>
              )}
            </div>
            <div className="md:col-span-4">
              {/* 2026-10-09 (user request): INDONESIAN_BANKS ~130 opsi — searchable wajib. */}
              <SearchableSelect
                testid={`bank-name-${i}`}
                value={r.bank_name}
                onChange={(v) => updateRow(i, { bank_name: v })}
                options={INDONESIAN_BANKS.map((b) => ({ value: b, label: b }))}
                placeholder="Pilih bank…"
                searchPlaceholder="Ketik nama bank…"
                emptyText="Bank tidak ditemukan"
                disabled={!canEdit}
              />
            </div>
            <div className="md:col-span-4">
              <Input
                data-testid={`bank-acc-${i}`}
                value={r.account_no}
                onChange={(e) => updateRow(i, { account_no: e.target.value.replace(/[^\d]/g, "") })}
                placeholder="1234567890"
                disabled={!canEdit}
                className="rounded-none h-8 text-xs tabular-nums"
              />
            </div>
            <div className="md:col-span-3 flex justify-end items-center gap-1">
              {canEdit && (
                <>
                  {r.id && (
                    <>
                      <button data-testid={`bank-move-up-${i}`} onClick={() => move(i, -1)} disabled={i === 0} className="p-1 text-slate-400 hover:text-[#0B1D3A] disabled:opacity-30" title="Naik"><ArrowUp size={12} /></button>
                      <button data-testid={`bank-move-down-${i}`} onClick={() => move(i, 1)} disabled={i === rows.length - 1} className="p-1 text-slate-400 hover:text-[#0B1D3A] disabled:opacity-30" title="Turun"><ArrowDown size={12} /></button>
                    </>
                  )}
                  {r.dirty && (
                    <button data-testid={`bank-save-${i}`} onClick={() => save(i)} disabled={busy} className="px-1.5 py-1 bg-[#FF5A1F] text-white text-[10px] uppercase font-semibold hover:bg-[#e04a13]"><Save size={11} /></button>
                  )}
                  <button data-testid={`bank-del-${i}`} onClick={() => removeRow(i)} className="p-1 text-slate-400 hover:text-rose-600" title="Hapus"><Trash2 size={12} /></button>
                </>
              )}
            </div>
            {r.bank_name && !isCompanyBank && (
              <div className="md:col-span-12 text-[10px] text-amber-700 inline-flex items-center gap-1">
                <AlertTriangle size={10} /> Bank non-perusahaan — potensi biaya admin transfer
              </div>
            )}
          </div>
        );
      })}
      {canEdit && (
        <button data-testid="btn-add-bank-account" onClick={addRow} className="text-[10px] uppercase tracking-wider font-semibold text-[#FF5A1F] hover:underline inline-flex items-center gap-1">
          <Plus size={12} /> Tambah Rekening
        </button>
      )}
    </div>
  );
}
